import { PathwaySettingsFormStore } from "@/admin/pathways/settings/PathwaySettingsForm"
import {
  AdminAddEntityTagPopoverContentMutation,
  AdminAddEntityTagPopoverContentMutation$data,
  CreateEntityTagInput,
} from "@/admin/settings/tabs/__generated__/AdminAddEntityTagPopoverContentMutation.graphql"
import AdminEntityTagsList from "@/admin/settings/tabs/AdminEntityTagsList"
import {
  invalidateTagRecords,
  temporaryTagId,
} from "@/admin/settings/tabs/entityTagUtils"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useFormStore } from "@/core/form/store/FormStore"
import { ExperienceSettingsFormStore } from "@/pricing/PricingFormFields"
import { ProductEntityTagInput } from "@/product/settings/ExperienceSettingsForm"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import Form from "@components/form/Form"
import { displaySuccessToast } from "@components/toast/ToastProvider"
import { DiscoButton, DiscoInput, DiscoSpinner } from "@disco-ui"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql } from "react-relay"

export type AdminAddTagFormState = CreateEntityTagInput

export type AdminAddEntityTagPopoverContentProps = TestIDProps & {
  form?: ExperienceSettingsFormStore | PathwaySettingsFormStore
  hideTagsList?: boolean
  hideAddButton?: boolean
  onSelect?: (tag: ProductEntityTagInput) => void
  skipCreation?: boolean
}

function AdminAddEntityTagPopoverContent({
  testid = "AdminAddEntityTagPopoverContent",
  form,
  hideTagsList = false,
  hideAddButton = false,
  onSelect,
  skipCreation = false,
}: AdminAddEntityTagPopoverContentProps) {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()

  const createEntityTagForm = useFormStore<
    AdminAddEntityTagPopoverContentMutation,
    AdminAddTagFormState
  >(
    graphql`
      mutation AdminAddEntityTagPopoverContentMutation($input: CreateEntityTagInput!) {
        response: createEntityTag(input: $input) {
          node {
            id
            name
            ...AdminEntityTagsTableRowFragment
            organization {
              entityTagsCount: entityTags(first: 1) {
                totalCount
              }
            }
          }
          errors {
            field
            message
          }
        }
      }
    `,
    { name: "" }
  )

  return (
    <Form
      testid={`${testid}.form`}
      onSubmit={handleSubmit}
      customClassName={classes.popover}
    >
      <div className={classes.content}>
        <DiscoInput
          data-testid={`${testid}.form.input`}
          placeholder={"Type to create a new tag"}
          classes={{ root: classes.input }}
          onChange={(e) => (createEntityTagForm.state.name = e.target.value)}
          value={createEntityTagForm.state.name}
          onKeyDown={(e) => {
            // Do not do anything on "Enter" key press, it could cause confusion to the
            // user, so force them to select the tag from the list, or create a new one
            if (e.key === "Enter") {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          fullWidth
        />

        {createEntityTagForm.state.name && !hideAddButton && (
          <DiscoButton
            onClick={handleSubmit}
            disabled={createEntityTagForm.disabled || createEntityTagForm.isSubmitting}
            shouldDisplaySpinner={createEntityTagForm.isSubmitting}
            testid={`${testid}.submit`}
            variant={"outlined"}
            color={"grey"}
          >
            {"Add"}
          </DiscoButton>
        )}
      </div>

      {!hideTagsList && (
        <AdminEntityTagsList
          form={form}
          createEntityTagForm={createEntityTagForm}
          onCreateTag={handleSubmit}
          onSelectTag={onSelect}
          classes={{ list: classes.list }}
        />
      )}
    </Form>
  )

  async function handleSubmit(): Promise<
    | AdminAddEntityTagPopoverContentMutation$data["response"]["node"]
    | ProductEntityTagInput
    | undefined
  > {
    if (skipCreation) {
      const { name } = createEntityTagForm.state
      createEntityTagForm.state.name = ""
      return { id: temporaryTagId(), name }
    }

    const { didSave, response } = await createEntityTagForm.submit(
      { name: createEntityTagForm.state.name },
      {
        updater: (store) => {
          const orgRecord = store.get(activeOrganization.id)
          if (!orgRecord) return

          invalidateTagRecords({ orgRecord })
        },
      }
    )
    if (!didSave || !response?.node) return undefined

    displaySuccessToast({
      message: "Tag created!",
      testid: `${testid}.success-message`,
    })

    if (onSelect) onSelect({ id: response.node.id, name: response.node.name })

    createEntityTagForm.state.name = ""
    return response.node
  }
}

const useStyles = makeUseStyles((theme) => ({
  popover: {
    alignItems: "flex-start",
    padding: theme.spacing(2),
    borderRadius: theme.measure.borderRadius.big,
  },
  content: {
    display: "flex",
    gap: theme.spacing(1),
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  },
  input: {
    border: theme.palette.constants.borderSmall,
  },
  list: {
    marginTop: theme.spacing(1.5),
  },
}))

function AdminAddEntityTagPopoverContentSkeleton() {
  return <DiscoSpinner size={"xs"} />
}

export default Relay.withSkeleton({
  component: observer(AdminAddEntityTagPopoverContent),
  skeleton: AdminAddEntityTagPopoverContentSkeleton,
})
